/**
 * Evidence validity, on screen.
 *
 * WHAT THIS IS FOR
 * An ISO certificate, a penetration-test report, a signed DPA and a training
 * record all stop being true on a date. A control marked COMPLIANT in January
 * that still reads COMPLIANT in December on the strength of a certificate which
 * lapsed in June is a false green — and an auditor who finds one stops trusting
 * every other number on the screen.
 *
 * HOW STRONG THE SIGNAL IS, AND WHY
 *
 *   The stored verdict is NEVER rewritten. `COMPLIANT` on an answer is a record
 *   of what was assessed at a point in time; a component that recoloured it
 *   would be claiming the assessment said something it did not, would destroy
 *   the record an auditor came to read, and would have to be undone the day the
 *   certificate is renewed. So the verdict stays, and the expiry is stated
 *   NEXT TO IT, loudly.
 *
 *   The strength is graded, because an undifferentiated red on everything is
 *   read as decoration within a week:
 *
 *     Every attached item expired   -> CRITICAL, role="alert", full-width
 *                                      banner above the controls. Nothing
 *                                      supports this claim today. This is the
 *                                      false green, and it is the one case that
 *                                      earns the strongest treatment.
 *     Some expired, some still hold -> SERIOUS, role="status". The claim is
 *                                      incomplete rather than unsupported;
 *                                      saying "unsupported" would be false and
 *                                      would teach people to ignore the banner.
 *     Only expiring soon            -> WARNING, role="status". Actionable, not
 *                                      yet a problem. This is the state whose
 *                                      whole purpose is to leave time to renew.
 *
 *   Colour is never the message. Every state ships an icon and a sentence, and
 *   the banner names the dates rather than gesturing at them, because the next
 *   thing the reader has to do is go and find that document.
 *
 * NOT_TRACKED IS NOT A PROBLEM. Most evidence — a screenshot, a config export —
 * has no meaningful expiry. It is rendered as "no expiry set", never as a
 * warning, and it must never be: turning the whole existing library amber on
 * the day this shipped would have been a worse lie than the one being fixed.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  type EvidenceLifecycleState,
  type EvidenceLifecycleSummary,
  isValidityWindowOrdered,
} from '@opencomply/shared';
import { api, ApiError } from '../lib/api';
import { useApiError } from '../lib/useApiError';
import { cn } from '../lib/cn';
import { Button, Pill, type Tone } from './ui';

// ---------------------------------------------------------------------------
//  Strings
//
//  Every string carries its English text here, so these screens render
//  correctly before the locale files are updated, and the merger has one object
//  to copy from. `{{x}}` is i18next interpolation. A key ending `_one`/`_other`
//  is a PLURAL and is selected by the `count` variable — a flat key would
//  render raw.
// ---------------------------------------------------------------------------

export const EVIDENCE_LIFECYCLE_STRINGS = {
  // --- State labels ---
  'evidenceLifecycle.state.NOT_TRACKED': 'No expiry set',
  'evidenceLifecycle.state.SCHEDULED': 'Not yet in force',
  'evidenceLifecycle.state.VALID': 'Valid',
  'evidenceLifecycle.state.EXPIRING_SOON': 'Expiring soon',
  'evidenceLifecycle.state.EXPIRED': 'EXPIRED',

  // --- Dates and counts ---
  'evidenceLifecycle.expiresToday': 'Expires today',
  'evidenceLifecycle.expiresInDays_one': 'Expires in {{count}} day',
  'evidenceLifecycle.expiresInDays_other': 'Expires in {{count}} days',
  'evidenceLifecycle.expiredToday': 'Expired today',
  'evidenceLifecycle.expiredDaysAgo_one': 'Expired {{count}} day ago',
  'evidenceLifecycle.expiredDaysAgo_other': 'Expired {{count}} days ago',
  'evidenceLifecycle.validUntilOn': 'Valid until {{date}}',
  'evidenceLifecycle.validFromOn': 'In force from {{date}}',
  'evidenceLifecycle.validBetween': 'Valid {{from}} to {{until}}',
  'evidenceLifecycle.noExpiry': 'No expiry recorded for this evidence.',

  // --- The answer-level banner ---
  'evidenceLifecycle.alertAllExpiredTitle': 'No valid evidence supports this any more',
  'evidenceLifecycle.alertAllExpiredBody':
    'Every item of evidence attached here has expired. Any control shown as applied is standing on a lapsed document, so the status below records what was assessed at the time — not what is true today. Renew the evidence, or reassess.',
  'evidenceLifecycle.alertSomeExpiredTitle_one': '{{count}} item of evidence has expired',
  'evidenceLifecycle.alertSomeExpiredTitle_other': '{{count}} items of evidence have expired',
  'evidenceLifecycle.alertSomeExpiredBody':
    'Some of the evidence here has lapsed while other items still hold. Check that what remains actually supports the status shown below.',
  'evidenceLifecycle.alertExpiringTitle_one': '{{count}} item of evidence expires within {{days}} days',
  'evidenceLifecycle.alertExpiringTitle_other': '{{count}} items of evidence expire within {{days}} days',
  'evidenceLifecycle.alertExpiringBody':
    'Renew it before it lapses. Once it does, the status shown below stops being supported.',
  'evidenceLifecycle.alertNextExpiry': 'Soonest expiry: {{date}}.',
  'evidenceLifecycle.alertScheduled_one':
    '{{count}} item is not yet in force and cannot support this today.',
  'evidenceLifecycle.alertScheduled_other':
    '{{count}} items are not yet in force and cannot support this today.',

  // --- Assessment-wide roll-up ---
  'evidenceLifecycle.submissionAlertTitle_one':
    '{{count}} answer rests on evidence that has expired',
  'evidenceLifecycle.submissionAlertTitle_other':
    '{{count}} answers rest on evidence that has expired',
  'evidenceLifecycle.submissionAlertBody':
    'The compliance figure above still counts these as met, because it reports what was assessed. Open each question marked below, renew the evidence, or reassess.',
  'evidenceLifecycle.submissionExpiringTitle_one':
    '{{count}} answer has evidence expiring within {{days}} days',
  'evidenceLifecycle.submissionExpiringTitle_other':
    '{{count}} answers have evidence expiring within {{days}} days',
  'evidenceLifecycle.submissionExpiringBody':
    'Renew the evidence before it lapses, or these answers will stop being supported.',
  'evidenceLifecycle.navExpired': 'evidence expired',
  'evidenceLifecycle.navExpiringSoon': 'evidence expiring soon',

  // --- Control-level marker ---
  'evidenceLifecycle.controlUnsupported': 'Evidence expired',
  'evidenceLifecycle.controlUnsupportedHint':
    'The evidence behind this control has expired. The status shown is what was assessed at the time, not what is true today.',

  // --- Editing ---
  'evidenceLifecycle.edit': 'Validity',
  'evidenceLifecycle.editTitle': 'When is this evidence valid?',
  'evidenceLifecycle.validFromLabel': 'Valid from (optional)',
  'evidenceLifecycle.validUntilLabel': 'Valid until (optional)',
  'evidenceLifecycle.editHint':
    'Leave both empty if this evidence does not expire. “Valid until” counts as valid for the whole of that day.',
  'evidenceLifecycle.save': 'Save',
  'evidenceLifecycle.clear': 'Clear both dates',
  'evidenceLifecycle.cancel': 'Cancel',
  'evidenceLifecycle.inverted': '“Valid until” cannot be earlier than “Valid from”.',
  'evidenceLifecycle.readOnly': 'This record is locked, so validity dates cannot be changed.',

  // --- Error codes with no shared translation yet ---
  'evidenceLifecycle.error.EVIDENCE_VALIDITY_WINDOW_INVERTED':
    '“Valid until” cannot be earlier than “Valid from”.',
  'evidenceLifecycle.error.EVIDENCE_VALIDITY_NO_CHANGE': 'Nothing was changed.',
  'evidenceLifecycle.error.EVIDENCE_VALIDITY_DATE_FORMAT': 'Enter a date as YYYY-MM-DD.',
  'evidenceLifecycle.error.EVIDENCE_VALIDITY_DATE_INVALID': 'That date does not exist.',
  'evidenceLifecycle.error.SUBMISSION_FINALIZED':
    'This assessment is finalised, so its evidence dates are part of a sealed record and cannot be changed.',
} as const;

export type LifecycleKey = keyof typeof EVIDENCE_LIFECYCLE_STRINGS;

/**
 * The BASE form of each plural key — what the code calls `tl()` with.
 *
 * i18next never looks up a bare key when `count` is passed: it looks up
 * `<key>_one` or `<key>_other`. So the caller says `expiresInDays` and the
 * resolver below has to find the English default under the suffixed name. Get
 * this wrong and the screen renders the literal key, which is the failure this
 * type exists to make hard.
 */
export type LifecyclePluralKey =
  | 'evidenceLifecycle.expiresInDays'
  | 'evidenceLifecycle.expiredDaysAgo'
  | 'evidenceLifecycle.alertSomeExpiredTitle'
  | 'evidenceLifecycle.alertExpiringTitle'
  | 'evidenceLifecycle.alertScheduled'
  | 'evidenceLifecycle.submissionAlertTitle'
  | 'evidenceLifecycle.submissionExpiringTitle';

/**
 * The inline English for a key, picking the right plural form.
 *
 * Needed because a single flat `defaultValue` would be used for BOTH plural
 * forms once the locale files carry the real translations — "1 items have
 * expired" is the kind of thing that reaches a customer.
 */
function englishDefault(key: string, count: unknown): string | undefined {
  const table = EVIDENCE_LIFECYCLE_STRINGS as Record<string, string | undefined>;
  const direct = table[key];
  if (direct !== undefined) return direct;
  if (typeof count === 'number') {
    // English has exactly two cardinal forms. Other languages have more, and
    // their own files supply them; this is only the pre-translation fallback.
    return table[`${key}${count === 1 ? '_one' : '_other'}`];
  }
  return undefined;
}

/** Translate with the built-in English as the fallback. */
export function useLifecycleStrings() {
  const { t, i18n } = useTranslation();
  const tl = (key: LifecycleKey | LifecyclePluralKey, options?: Record<string, unknown>) =>
    t(key, { defaultValue: englishDefault(key, options?.count), ...options });
  return { tl, locale: i18n.language };
}

// ---------------------------------------------------------------------------
//  Shared presentation
// ---------------------------------------------------------------------------

/**
 * Tone per state.
 *
 * NOT_TRACKED and VALID are both quiet, and deliberately different from each
 * other only in wording: "valid until March" is a positive statement, "no
 * expiry set" is the absence of one. Neither is a problem, so neither gets a
 * colour that competes with the ones that are.
 */
export const TONE_FOR_LIFECYCLE: Record<EvidenceLifecycleState, Tone> = {
  NOT_TRACKED: 'neutral',
  SCHEDULED: 'warning',
  VALID: 'good',
  EXPIRING_SOON: 'warning',
  EXPIRED: 'critical',
};

export interface EvidenceValidityFields {
  validFrom: string | null;
  validUntil: string | null;
  lifecycleState: EvidenceLifecycleState;
  daysUntilExpiry?: number | null;
}

function useDateFormat(locale: string) {
  return (iso: string) =>
    new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
}

/**
 * The state of one item, as a pill plus a plain-language sentence.
 *
 * The pill alone would be colour-plus-one-word; what a person needs is the
 * date. Both are rendered, always.
 */
export function LifecycleBadge({ item }: { item: EvidenceValidityFields }) {
  const { tl, locale } = useLifecycleStrings();
  const fmt = useDateFormat(locale);
  const days = item.daysUntilExpiry ?? null;

  let detail: string | null = null;
  if (item.lifecycleState === 'EXPIRED') {
    const ago = days === null ? null : Math.abs(days);
    detail =
      ago === 0 || ago === null
        ? tl('evidenceLifecycle.expiredToday')
        : tl('evidenceLifecycle.expiredDaysAgo', { count: ago });
  } else if (item.lifecycleState === 'EXPIRING_SOON') {
    detail =
      days === 0 || days === null
        ? tl('evidenceLifecycle.expiresToday')
        : tl('evidenceLifecycle.expiresInDays', { count: days });
  } else if (item.lifecycleState === 'SCHEDULED' && item.validFrom) {
    detail = tl('evidenceLifecycle.validFromOn', { date: fmt(item.validFrom) });
  } else if (item.lifecycleState === 'VALID' && item.validUntil) {
    detail = tl('evidenceLifecycle.validUntilOn', { date: fmt(item.validUntil) });
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Pill tone={TONE_FOR_LIFECYCLE[item.lifecycleState]}>
        {tl(`evidenceLifecycle.state.${item.lifecycleState}` as LifecycleKey)}
      </Pill>
      {detail && <span className="text-xs text-ink-secondary">{detail}</span>}
      {/* The exact window, for anyone who has to go and find the document. */}
      {item.validUntil && item.validFrom && (
        <span className="text-[11px] text-ink-muted">
          {tl('evidenceLifecycle.validBetween', {
            from: fmt(item.validFrom),
            until: fmt(item.validUntil),
          })}
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
//  The answer / plan level alert
// ---------------------------------------------------------------------------

type AlertLevel = 'critical' | 'serious' | 'warning';

/**
 * Decide whether there is anything to say, and how loudly.
 *
 * Exported because the same decision has to be made in more than one place —
 * the evidence panel and the assessment screen — and two copies of this
 * `if` ladder would eventually disagree about what "at risk" means.
 */
export function lifecycleAlertLevel(
  summary: EvidenceLifecycleSummary | null | undefined,
): AlertLevel | null {
  if (!summary || summary.total === 0) return null;
  if (summary.allEvidenceExpired) return 'critical';
  if (summary.expired > 0) return 'serious';
  if (summary.expiringSoon > 0) return 'warning';
  // Evidence that exists but has not come into force yet is worth saying, at
  // the mildest level: it cannot support a claim about today.
  if (summary.scheduled > 0 && summary.valid === 0 && summary.notTracked === 0) return 'warning';
  return null;
}

/**
 * The banner.
 *
 * Renders nothing at all when there is nothing to report. That matters more
 * than it sounds: a component that always draws a box — even a green "all
 * good" one — trains the eye to skip that region of the page, and the one time
 * it turns red nobody sees it.
 */
export function EvidenceExpiryAlert({
  summary,
  warningDays,
  className,
}: {
  summary: EvidenceLifecycleSummary | null | undefined;
  /** The server's configured window, so the copy does not hard-code 30. */
  warningDays?: number;
  className?: string;
}) {
  const { tl, locale } = useLifecycleStrings();
  const fmt = useDateFormat(locale);
  const level = lifecycleAlertLevel(summary);
  if (!level || !summary) return null;

  let title: string;
  let body: string;

  if (level === 'critical') {
    title = tl('evidenceLifecycle.alertAllExpiredTitle');
    body = tl('evidenceLifecycle.alertAllExpiredBody');
  } else if (level === 'serious') {
    title = tl('evidenceLifecycle.alertSomeExpiredTitle', { count: summary.expired });
    body = tl('evidenceLifecycle.alertSomeExpiredBody');
  } else if (summary.expiringSoon > 0) {
    title = tl('evidenceLifecycle.alertExpiringTitle', {
      count: summary.expiringSoon,
      days: warningDays ?? 30,
    });
    body = tl('evidenceLifecycle.alertExpiringBody');
  } else {
    title = tl('evidenceLifecycle.alertScheduled', { count: summary.scheduled });
    body = '';
  }

  return (
    <div
      // A claim with nothing valid behind it is announced; the milder states are
      // polite status updates. Both are read by a screen reader, but only the
      // first interrupts.
      role={level === 'critical' ? 'alert' : 'status'}
      className={cn(
        'rounded-xl border px-3 py-2.5',
        level === 'critical' && 'border-critical/50 bg-critical/[0.09]',
        level === 'serious' && 'border-serious/50 bg-serious/[0.09]',
        level === 'warning' && 'border-warning/50 bg-warning/[0.09]',
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <span
          aria-hidden
          className={cn(
            'mt-0.5 shrink-0',
            level === 'critical' && 'text-critical',
            level === 'serious' && 'text-serious',
            level === 'warning' && 'text-warning',
          )}
        >
          <WarnGlyph />
        </span>
        <div className="min-w-0">
          <p
            className={cn(
              'text-sm font-semibold',
              level === 'critical' && 'text-critical',
              level === 'serious' && 'text-serious',
              level === 'warning' && 'text-warning',
            )}
          >
            {title}
          </p>
          {body && <p className="mt-1 text-xs leading-relaxed text-ink-secondary">{body}</p>}
          {summary.nextExpiryAt && level !== 'critical' && (
            <p className="mt-1 text-[11px] text-ink-muted">
              {tl('evidenceLifecycle.alertNextExpiry', { date: fmt(summary.nextExpiryAt) })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The marker that sits beside a control's own status pill.
 *
 * Small on purpose: the banner above already carries the explanation, and
 * repeating it per control would drown the list. What this adds is the link
 * between "something expired" and "THIS control is the one relying on it",
 * which the banner alone cannot express.
 */
export function ControlEvidenceWarning({ className }: { className?: string }) {
  const { tl } = useLifecycleStrings();
  return (
    <span
      className={cn('inline-flex items-center gap-1', className)}
      title={tl('evidenceLifecycle.controlUnsupportedHint')}
    >
      <Pill tone="critical">{tl('evidenceLifecycle.controlUnsupported')}</Pill>
    </span>
  );
}

// ---------------------------------------------------------------------------
//  Editing one item's window
// ---------------------------------------------------------------------------

/** `<input type="date">` wants YYYY-MM-DD; the API returns a full ISO string. */
function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  // UTC parts, matching how the server stores a date-only boundary (midnight
  // UTC). Using local parts here would shift the date by one for anyone west
  // of Greenwich.
  return date.toISOString().slice(0, 10);
}

/**
 * Inline editor for one item's validity.
 *
 * Two `<input type="date">` fields rather than a custom picker: the native
 * control is keyboard-accessible, localised by the browser, and understood by
 * every assistive technology already. A hand-rolled calendar would be prettier
 * and worse.
 */
export function ValidityEditor({
  evidenceId,
  validFrom,
  validUntil,
  readOnly,
  onSaved,
}: {
  evidenceId: string;
  validFrom: string | null;
  validUntil: string | null;
  readOnly?: boolean;
  onSaved?: () => void;
}) {
  const { tl } = useLifecycleStrings();
  const describeApiError = useApiError();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(() => toDateInput(validFrom));
  const [until, setUntil] = useState(() => toDateInput(validUntil));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: { validFrom: string | null; validUntil: string | null }) =>
      api.patch(`/evidence/${evidenceId}/validity`, body),
    onSuccess: () => {
      setError(null);
      setOpen(false);
      // Both the panel's own list and the assessment payload carry the dates,
      // so both are refreshed — otherwise the banner above would keep showing
      // the old verdict until a reload.
      void queryClient.invalidateQueries({ queryKey: ['evidence'] });
      void queryClient.invalidateQueries({ queryKey: ['submission'] });
      onSaved?.();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const key = `evidenceLifecycle.error.${err.code}` as LifecycleKey;
        if (key in EVIDENCE_LIFECYCLE_STRINGS) {
          setError(tl(key));
          return;
        }
      }
      setError(describeApiError(err));
    },
  });

  if (readOnly) return null;

  if (!open) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        {tl('evidenceLifecycle.edit')}
      </Button>
    );
  }

  const submit = () => {
    setError(null);
    const payload = {
      validFrom: from ? from : null,
      validUntil: until ? until : null,
    };
    // Checked here with the same shared function the server uses, so the user
    // finds out before a round trip. The server checks again regardless.
    if (!isValidityWindowOrdered(payload)) {
      setError(tl('evidenceLifecycle.inverted'));
      return;
    }
    save.mutate(payload);
  };

  return (
    <form
      // `basis-full` so that, dropped into the row of action buttons, the form
      // wraps onto its own full-width line instead of squeezing in beside them.
      className="basis-full w-full rounded-xl border border-line p-3 space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      noValidate
    >
      <p className="text-xs font-medium text-ink-secondary">{tl('evidenceLifecycle.editTitle')}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <label className="oc-label" htmlFor={`ev-from-${evidenceId}`}>
            {tl('evidenceLifecycle.validFromLabel')}
          </label>
          <input
            id={`ev-from-${evidenceId}`}
            className="oc-input"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div>
          <label className="oc-label" htmlFor={`ev-until-${evidenceId}`}>
            {tl('evidenceLifecycle.validUntilLabel')}
          </label>
          <input
            id={`ev-until-${evidenceId}`}
            className="oc-input"
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
          />
        </div>
      </div>
      <p className="text-[11px] leading-snug text-ink-muted">{tl('evidenceLifecycle.editHint')}</p>
      {error && (
        <p role="alert" className="text-xs text-critical">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" size="sm" disabled={save.isPending}>
          {tl('evidenceLifecycle.save')}
        </Button>
        <Button
          size="sm"
          disabled={save.isPending || (!from && !until)}
          onClick={() => {
            setFrom('');
            setUntil('');
            save.mutate({ validFrom: null, validUntil: null });
          }}
        >
          {tl('evidenceLifecycle.clear')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setOpen(false);
            setError(null);
            setFrom(toDateInput(validFrom));
            setUntil(toDateInput(validUntil));
          }}
        >
          {tl('evidenceLifecycle.cancel')}
        </Button>
      </div>
    </form>
  );
}

// --- Glyph (kept local: used only here) --------------------------------------

const WarnGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className="w-5 h-5"
  >
    <path d="M12 3 2 20h20L12 3Z" />
    <path d="M12 10v4" />
    <path d="M12 17.5v.01" />
  </svg>
);
